import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "../auth/AuthContext";
import { useNotification } from "../notifications/NotificationContext";
import { placeTypeRu } from "../utils/placeTypeRu";
import { validatePhotoSelection } from "../utils/uploadValidation";
import { buildForecastTable } from "../utils/forecastDatetime";
import { getWeatherApiErrorMessage, isWeatherNoDataError } from "../utils/weatherApi";
import {
    WEATHER_CACHE_MS,
    getDayHourFromTimestamp,
    pickBestSlot,
    pickWeatherIconKey,
    resolveSpotImage,
    toDateTimeLocalValue,
    toSafeArray,
} from "../utils/spotDetailUtils";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

export function useSpotDetail(spotId, options = {}) {
    const {
        countView = true,
        includeVideos = false,
        includeNotes = false,
        onEngagementChange,
        onSpotChange,
        weatherLazy = false,
        controlledTab = null,
        controlledPhotoTab = null,
        controlledWeatherType = null,
        initialSpot = null,
    } = options;

    const { showNotification } = useNotification();
    const { isAuthenticated } = useAuth();

    const [spot, setSpot] = useState(initialSpot);
    const [loading, setLoading] = useState(!initialSpot);
    const [comments, setComments] = useState([]);
    const [message, setMessage] = useState("");
    const [selectedFish, setSelectedFish] = useState(null);
    const [forecast, setForecast] = useState(null);
    const [forecastLoading, setForecastLoading] = useState(false);
    const [forecastError, setForecastError] = useState("");
    const [selectedForecastSlot, setSelectedForecastSlot] = useState(null);
    const [nearestWeather, setNearestWeather] = useState(null);
    const [nearestWeatherLoading, setNearestWeatherLoading] = useState(false);
    const [nearestWeatherError, setNearestWeatherError] = useState("");
    const [nearestWeatherFetchedAt, setNearestWeatherFetchedAt] = useState(0);
    const [commentText, setCommentText] = useState("");
    const [commentForm, setCommentForm] = useState({
        startTime: "",
        endTime: "",
        whatCaught: "",
        baitUsed: "",
        fishingType: "0",
        groundbait: "",
        depth: "",
    });
    const [commentFiles, setCommentFiles] = useState({ place: [], catch: [], depth: [] });
    const [commentSubmitting, setCommentSubmitting] = useState(false);
    const [commentError, setCommentError] = useState("");
    const [commentFieldErrors, setCommentFieldErrors] = useState({});
    const [commentFileErrors, setCommentFileErrors] = useState({});
    const [commentFormOpen, setCommentFormOpen] = useState(false);
    const [actionMessage, setActionMessage] = useState("");
    const [liked, setLiked] = useState(!!initialSpot?.userLiked);
    const [favorited, setFavorited] = useState(!!initialSpot?.userFavorited);
    const [likePending, setLikePending] = useState(false);
    const [favoritePending, setFavoritePending] = useState(false);
    const [reportModalOpen, setReportModalOpen] = useState(false);
    const [routes, setRoutes] = useState([]);
    const [selectedRouteIndex, setSelectedRouteIndex] = useState(0);
    const [videos, setVideos] = useState([]);
    const [notes, setNotes] = useState([]);
    const [internalPhotoType, setInternalPhotoType] = useState("place");
    const [noteFormOpen, setNoteFormOpen] = useState(false);
    const [noteText, setNoteText] = useState("");
    const [editingNoteId, setEditingNoteId] = useState(null);
    const [editingNoteText, setEditingNoteText] = useState("");
    const [notePendingId, setNotePendingId] = useState(null);
    const [noteError, setNoteError] = useState("");
    const [videoUrl, setVideoUrl] = useState("");
    const [videoTitle, setVideoTitle] = useState("");
    const [internalActiveTab, setInternalActiveTab] = useState("main");
    const [showAllFish, setShowAllFish] = useState(false);
    const [internalWeatherType, setInternalWeatherType] = useState("bite");
    const [weatherTimeline, setWeatherTimeline] = useState([]);
    const [weatherTimelineLoading, setWeatherTimelineLoading] = useState(false);
    const [weatherTimelineError, setWeatherTimelineError] = useState("");
    const [weatherTimelineFetchedAt, setWeatherTimelineFetchedAt] = useState(0);

    const activeTab = controlledTab?.activeTab ?? internalActiveTab;
    const setActiveTab = controlledTab?.setActiveTab ?? setInternalActiveTab;
    const photoType = controlledPhotoTab?.photoType ?? internalPhotoType;
    const setPhotoType = controlledPhotoTab?.setPhotoType ?? setInternalPhotoType;
    const weatherType = controlledWeatherType?.weatherType ?? internalWeatherType;
    const setWeatherType = controlledWeatherType?.setWeatherType ?? setInternalWeatherType;
    const isTabControlled = Boolean(controlledTab);
    const isPhotoTabControlled = Boolean(controlledPhotoTab);
    const isWeatherTypeControlled = Boolean(controlledWeatherType);
    const shouldFetchNearestWeather = !weatherLazy || activeTab === "weather";
    const shouldFetchWeatherTimeline = activeTab === "weather" && weatherType === "weather";
    const shouldLoadBiteForecast = activeTab === "weather" && weatherType === "bite";

    const hasCommentFileErrors = Object.values(commentFileErrors).some(Boolean);
    const likePendingRef = useRef(false);
    const favoritePendingRef = useRef(false);
    const loadedSpotIdRef = useRef(null);
    const spotRef = useRef(spot);
    spotRef.current = spot;
    const maxFishingDateTime = toDateTimeLocalValue(new Date());

    const setCommentFormField = (name, value) => {
        setCommentForm((prev) => ({ ...prev, [name]: value }));
        setCommentFieldErrors((prev) => {
            if (!prev[name]) return prev;
            const { [name]: _removed, ...rest } = prev;
            return rest;
        });
    };

    const commentInputClass = (name, baseClass = "ui-input") => `${baseClass}${commentFieldErrors[name] ? " is-invalid" : ""}`;

    const validateCommentForm = () => {
        const errors = {};
        const now = new Date();
        const start = commentForm.startTime ? new Date(commentForm.startTime) : null;
        const end = commentForm.endTime ? new Date(commentForm.endTime) : null;

        if (start && Number.isNaN(start.getTime())) {
            errors.startTime = "Дата начала рыбалки указана некорректно.";
        } else if (start && start > now) {
            errors.startTime = "Дата начала рыбалки не может быть позже текущей даты.";
        }

        if (end && Number.isNaN(end.getTime())) {
            errors.endTime = "Дата окончания рыбалки указана некорректно.";
        } else if (end && end > now) {
            errors.endTime = "Дата окончания рыбалки не может быть позже текущей даты.";
        }

        if (!errors.startTime && !errors.endTime && start && end && start > end) {
            errors.startTime = "Дата начала рыбалки не может быть позже даты окончания.";
            errors.endTime = "Дата окончания должна быть не раньше даты начала.";
        }

        const depthText = String(commentForm.depth || "").trim();
        if (depthText !== "") {
            const depth = Number(depthText);
            if (!Number.isFinite(depth) || depth < 0 || depth > 100) {
                errors.depth = "Глубина должна быть от 0 до 100.";
            }
        }

        if (!commentText.trim()) {
            errors.comment = "Введите текст отчёта.";
        }

        setCommentFieldErrors(errors);

        return Object.keys(errors).length === 0;
    };

    const handleCommentPhotoChange = (type, fileList) => {
        const result = validatePhotoSelection(type, fileList);
        setCommentFiles((prev) => ({ ...prev, [type]: result.files }));
        setCommentFileErrors((prev) => ({ ...prev, [type]: result.error }));
    };

    useEffect(() => {
        loadedSpotIdRef.current = null;
        setSpot(initialSpot ?? null);
        setLoading(!initialSpot);
    }, [spotId, initialSpot]);

    const load = useCallback(async () => {
        if (!spotId) return;
        const isBackgroundRefresh = loadedSpotIdRef.current === spotId && !!spotRef.current;
        if (!isBackgroundRefresh) {
            const showLoading = !spotRef.current
                || (loadedSpotIdRef.current !== null && loadedSpotIdRef.current !== spotId);
            if (showLoading) setLoading(true);
        }
        try {
            const [spotRes, commentsRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/api/spots/${spotId}`, { params: { countView }, withCredentials: true }),
                axios.get(`${API_BASE_URL}/api/spots/${spotId}/comments`),
            ]);
            const secondaryRequests = [axios.get(`${API_BASE_URL}/api/spots/${spotId}/routes`)];
            if (includeVideos) {
                secondaryRequests.push(axios.get(`${API_BASE_URL}/api/spots/${spotId}/videos`));
            }
            const secondaryResults = await Promise.all(secondaryRequests);
            const routesRes = secondaryResults[0];
            const videosRes = includeVideos ? secondaryResults[1] : { data: [] };

            setSpot(spotRes.data);
            setLiked(!!spotRes.data?.userLiked);
            setFavorited(!!spotRes.data?.userFavorited);
            setComments(toSafeArray(commentsRes.data));
            setRoutes(toSafeArray(routesRes.data));
            setSelectedRouteIndex(0);
            setVideos(toSafeArray(videosRes.data));
            if (!isBackgroundRefresh) {
                setSelectedFish(null);
                setForecast(null);
                setForecastError("");
                setSelectedForecastSlot(null);
                setNearestWeather(null);
                setNearestWeatherFetchedAt(0);
                setNearestWeatherError("");
                if (!isPhotoTabControlled) setInternalPhotoType("place");
                if (!isTabControlled) setInternalActiveTab("main");
                setShowAllFish(false);
                if (!isWeatherTypeControlled) setInternalWeatherType("bite");
                setWeatherTimeline([]);
                setWeatherTimelineFetchedAt(0);
                setCommentFormOpen(false);
                setCommentFieldErrors({});
                setCommentFileErrors({});
            }
            setMessage("");
            loadedSpotIdRef.current = spotId;
        } catch {
            setMessage("Не удалось загрузить страницу точки.");
        } finally {
            setLoading(false);
        }
    }, [spotId, countView, includeVideos, isTabControlled, isPhotoTabControlled, isWeatherTypeControlled]);

    useEffect(() => {
        if (!includeNotes || !isAuthenticated || !spotId) {
            setNotes([]);
            return;
        }
        axios.get(`${API_BASE_URL}/api/notes/spots/${spotId}`, { withCredentials: true }).then((res) => {
            setNotes(res.data || []);
            setEditingNoteId(null);
            setEditingNoteText("");
            setNoteError("");
        }).catch(() => {});
    }, [spotId, isAuthenticated, includeNotes]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (!routes.length) {
            setSelectedRouteIndex(0);
            return;
        }
        if (selectedRouteIndex > routes.length - 1) {
            setSelectedRouteIndex(0);
        }
    }, [routes, selectedRouteIndex]);

    useEffect(() => {
        if (!shouldFetchNearestWeather) return undefined;
        const lat = Number(spot?.latitude);
        const lon = Number(spot?.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return undefined;
        if (nearestWeatherFetchedAt && Date.now() - nearestWeatherFetchedAt < WEATHER_CACHE_MS) return undefined;

        let cancelled = false;
        setNearestWeatherLoading(true);
        setNearestWeatherError("");
        axios.get(`${API_BASE_URL}/api/weather/nearest`, { params: { lat, lon }, skipAuth: true })
            .then((response) => {
                if (cancelled) return;
                setNearestWeather(response?.data || null);
                setNearestWeatherFetchedAt(Date.now());
            })
            .catch((error) => {
                if (cancelled) return;
                if (isWeatherNoDataError(error)) {
                    setNearestWeather(null);
                    setNearestWeatherFetchedAt(Date.now());
                    setNearestWeatherError("");
                    return;
                }
                setNearestWeatherError(getWeatherApiErrorMessage(error, "Не удалось загрузить погоду."));
            })
            .finally(() => {
                if (!cancelled) setNearestWeatherLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [shouldFetchNearestWeather, spot?.latitude, spot?.longitude, nearestWeatherFetchedAt]);

    const loadForecast = useCallback(async (fishItem) => {
        if (!fishItem?.id || !spotId) return;
        setSelectedFish(fishItem);
        setForecast(null);
        setForecastError("");
        setSelectedForecastSlot(null);
        setForecastLoading(true);
        try {
            const { data } = await axios.get(`${API_BASE_URL}/api/forecast/spots/${spotId}/fish/${fishItem.id}`, { skipAuth: true });
            setForecast(data || null);
            setSelectedForecastSlot(pickBestSlot(data));
        } catch (e) {
            setForecastError(e?.response?.data?.error || "Не удалось загрузить прогноз клёва.");
        } finally {
            setForecastLoading(false);
        }
    }, [spotId]);

    useEffect(() => {
        if (!shouldLoadBiteForecast) return;
        const spotFish = toSafeArray(spot?.fish);
        if (!spotFish.length || selectedFish?.id) return;
        loadForecast(spotFish[0]);
    }, [shouldLoadBiteForecast, loadForecast, selectedFish?.id, spot?.fish]);

    useEffect(() => {
        setNearestWeather(null);
        setNearestWeatherFetchedAt(0);
        setNearestWeatherError("");
        setWeatherTimeline([]);
        setWeatherTimelineFetchedAt(0);
        setWeatherTimelineError("");
    }, [spot?.latitude, spot?.longitude]);

    useEffect(() => {
        if (!shouldFetchWeatherTimeline) return undefined;
        const lat = Number(spot?.latitude);
        const lon = Number(spot?.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return undefined;
        if (weatherTimelineFetchedAt && Date.now() - weatherTimelineFetchedAt < WEATHER_CACHE_MS) return undefined;

        let cancelled = false;
        setWeatherTimelineLoading(true);
        setWeatherTimelineError("");
        axios.get(`${API_BASE_URL}/api/weather/nearest-forecast`, { params: { lat, lon }, skipAuth: true })
            .then((response) => {
                if (cancelled) return;
                setWeatherTimeline(Array.isArray(response?.data?.timeline) ? response.data.timeline : []);
                setWeatherTimelineFetchedAt(Date.now());
            })
            .catch((error) => {
                if (cancelled) return;
                if (isWeatherNoDataError(error)) {
                    setWeatherTimeline([]);
                    setWeatherTimelineFetchedAt(Date.now());
                    setWeatherTimelineError("");
                    return;
                }
                setWeatherTimelineError(getWeatherApiErrorMessage(error, "Не удалось загрузить прогноз погоды."));
            })
            .finally(() => {
                if (!cancelled) setWeatherTimelineLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [shouldFetchWeatherTimeline, spot?.latitude, spot?.longitude, weatherTimelineFetchedAt]);

    useEffect(() => {
        if (onSpotChange) onSpotChange(spot);
    }, [spot, onSpotChange]);

    const forecastTable = useMemo(
        () => buildForecastTable(forecast?.timeline),
        [forecast],
    );

    const weatherHourColumns = useMemo(() => ["00", "03", "06", "09", "12", "15", "18", "21"], []);

    const weatherForecastTable = useMemo(() => {
        const slots = Array.isArray(weatherTimeline) ? weatherTimeline : [];
        if (!slots.length) return null;
        const allowedHours = new Set(weatherHourColumns);
        const dayMap = {};

        slots.forEach((slot) => {
            const parts = getDayHourFromTimestamp(slot?.timestamp);
            if (!parts) return;
            const { dayKey, hourKey } = parts;
            if (!allowedHours.has(hourKey)) return;
            dayMap[`${dayKey}_${hourKey}`] = slot;
        });

        const dayKeys = Array.from(new Set(Object.keys(dayMap).map((key) => key.split("_")[0]))).sort();
        if (!dayKeys.length) return null;

        return { dayKeys, hours: weatherHourColumns, map: dayMap };
    }, [weatherHourColumns, weatherTimeline]);

    const submitComment = async (event) => {
        event.preventDefault();
        if (!spotId) {
            return;
        }
        const text = commentText.trim();
        if (!validateCommentForm()) {
            setCommentError(text ? "Проверьте поля отчёта." : "Не заполнены обязательные поля.");
            return;
        }
        if (hasCommentFileErrors) {
            setCommentError("Проверьте выбранные файлы.");
            return;
        }
        setCommentSubmitting(true);
        setCommentError("");
        try {
            const formData = new FormData();
            formData.append("spot-id", String(spotId));
            formData.append("comment", text);
            if (commentForm.startTime) formData.append("startTime", commentForm.startTime);
            if (commentForm.endTime) formData.append("endTime", commentForm.endTime);
            formData.append("whatCaught", commentForm.whatCaught);
            formData.append("baitUsed", commentForm.baitUsed);
            formData.append("fishingType", commentForm.fishingType);
            formData.append("groundbait", commentForm.groundbait);
            formData.append("depth", commentForm.depth);
            commentFiles.place.forEach((file) => formData.append("place_photos[]", file));
            commentFiles.catch.forEach((file) => formData.append("catch_photos[]", file));
            commentFiles.depth.forEach((file) => formData.append("depth_photos[]", file));

            await axios.post(`${API_BASE_URL}/api/comments`, formData, { withCredentials: true });
            const commentsRes = await axios.get(`${API_BASE_URL}/api/spots/${spotId}/comments`);
            setComments(commentsRes.data || []);
            setCommentText("");
            setCommentForm({ startTime: "", endTime: "", whatCaught: "", baitUsed: "", fishingType: "0", groundbait: "", depth: "" });
            setCommentFiles({ place: [], catch: [], depth: [] });
            setCommentFormOpen(false);
            setCommentFieldErrors({});
            setCommentFileErrors({});
            showNotification({ type: "success", text: "Отчёт отправлен. После проверки модератором он появится на странице." });
        } catch (e) {
            setCommentError(e?.response?.data?.error || "Не удалось отправить отчёт. Войдите в аккаунт.");
        } finally {
            setCommentSubmitting(false);
        }
    };

    const mapTitle = spot?.place?.name || (spot ? `Точка #${spot.id}` : "");
    const editSuggestionPath = spotId ? `/spots/${spotId}/edit-suggestion` : "";

    const openLoginModalForComments = () => {
        window.dispatchEvent(new CustomEvent("auth:open-login-modal", {
            detail: {
                nextPath: `/spots/${spotId}`,
                title: "Войдите, чтобы оставить отчёт",
            },
        }));
    };

    const requireAuthForEditSuggestion = (event) => {
        if (isAuthenticated) return;
        event.preventDefault();
        window.dispatchEvent(new CustomEvent("auth:open-login-modal", {
            detail: {
                nextPath: editSuggestionPath,
                title: "Необходима авторизация",
            },
        }));
    };

    const placeLabel = spot?.place?.name ? ` на ${spot.place.name}` : "";
    const fish = toSafeArray(spot?.fish);
    const visibleComments = toSafeArray(comments).filter((comment) => comment.published !== false);
    const selectedRoute = routes[selectedRouteIndex] || routes[0] || null;
    const mapRoutes = selectedRoute ? [selectedRoute] : [];
    const placePhotos = toSafeArray(spot?.placePhotos);
    const catchPhotos = toSafeArray(spot?.catchPhotos);
    const deepPhotos = toSafeArray(spot?.deepPhotos);
    const photoSource = photoType === "catch" ? catchPhotos : photoType === "deep" ? deepPhotos : placePhotos;
    const hasPhotos = placePhotos.length > 0 || catchPhotos.length > 0 || deepPhotos.length > 0;
    const heroImage = spot ? resolveSpotImage(spot) : "";
    const depthLabel = spot
        ? (spot.maxDepth ?? (spot.depthMax != null ? `${spot.depthMax} м` : null) ?? "-")
        : "-";
    const activeWeather = spot?.weather || spot?.weatherData || {};
    const weatherSource = nearestWeather?.weather || activeWeather || {};
    const weatherIconKey = pickWeatherIconKey({ ...nearestWeather, ...weatherSource });
    const precipitationValue = weatherSource.precipitationProbability ?? weatherSource.precipitation ?? 0;
    const cloudinessValue = weatherSource.cloudCover ?? weatherSource.cloudiness;
    const today = new Date().toISOString().slice(0, 10);
    const activeBans = toSafeArray(spot?.fishBans || spot?.fishBanPeriods).filter((ban) => {
        if (!ban?.startDate || !ban?.endDate) return false;
        return ban.startDate <= today && ban.endDate >= today;
    });

    const toggleLike = async () => {
        if (!spotId || likePendingRef.current) return;
        likePendingRef.current = true;
        setLikePending(true);
        try {
            let nextLiked;
            if (liked) {
                await axios.delete(`${API_BASE_URL}/api/likes`, { data: { entityType: "spot", entityId: Number(spotId) }, withCredentials: true });
                nextLiked = false;
                setLiked(false);
                setSpot((prev) => prev ? { ...prev, userLiked: false, likesCount: Math.max(0, Number(prev.likesCount ?? 0) - 1) } : prev);
            } else {
                await axios.post(`${API_BASE_URL}/api/likes`, { entityType: "spot", entityId: Number(spotId) }, { withCredentials: true });
                nextLiked = true;
                setLiked(true);
                setSpot((prev) => prev ? { ...prev, userLiked: true, likesCount: Number(prev.likesCount ?? 0) + 1 } : prev);
            }
            setActionMessage("");
            onEngagementChange?.({ liked: nextLiked, favorited, spot });
        } catch (e) {
            setActionMessage(e?.response?.data?.error || "Не удалось поставить лайк.");
        } finally {
            likePendingRef.current = false;
            setLikePending(false);
        }
    };

    const toggleFavorite = async () => {
        if (!spotId || favoritePendingRef.current) return;
        favoritePendingRef.current = true;
        setFavoritePending(true);
        try {
            let nextFavorited;
            if (favorited) {
                await axios.delete(`${API_BASE_URL}/api/favorites/spots/${spotId}`, { withCredentials: true });
                nextFavorited = false;
                setFavorited(false);
                setSpot((prev) => prev ? { ...prev, userFavorited: false, favoritesCount: Math.max(0, Number(prev.favoritesCount ?? 0) - 1) } : prev);
            } else {
                await axios.post(`${API_BASE_URL}/api/favorites/spots/${spotId}`, {}, { withCredentials: true });
                nextFavorited = true;
                setFavorited(true);
                setSpot((prev) => prev ? { ...prev, userFavorited: true, favoritesCount: Number(prev.favoritesCount ?? 0) + 1 } : prev);
            }
            setActionMessage("");
            onEngagementChange?.({ liked, favorited: nextFavorited, spot });
        } catch (e) {
            setActionMessage(e?.response?.data?.error || "Не удалось изменить избранное.");
        } finally {
            favoritePendingRef.current = false;
            setFavoritePending(false);
        }
    };

    const openReportModal = () => setReportModalOpen(true);

    const reloadNotes = async () => {
        const notesRes = await axios.get(`${API_BASE_URL}/api/notes/spots/${spotId}`, { withCredentials: true });
        setNotes(notesRes.data || []);
    };

    const createNote = async () => {
        if (!includeNotes || !noteText.trim()) return;
        setNoteError("");
        await axios.post(`${API_BASE_URL}/api/notes`, { fishingPointId: Number(spotId), text: noteText }, { withCredentials: true });
        await reloadNotes();
        setNoteText("");
        setNoteFormOpen(false);
    };

    const startEditNote = (note) => {
        setEditingNoteId(note.id);
        setEditingNoteText(note.text || "");
        setNoteError("");
    };

    const updateNote = async (noteId) => {
        if (!includeNotes) return;
        const text = editingNoteText.trim();
        if (!text) return;
        setNotePendingId(noteId);
        setNoteError("");
        try {
            await axios.put(`${API_BASE_URL}/api/notes/${noteId}`, { text }, { withCredentials: true });
            await reloadNotes();
            setEditingNoteId(null);
            setEditingNoteText("");
        } catch (e) {
            setNoteError(e?.response?.data?.error || "Не удалось сохранить заметку.");
        } finally {
            setNotePendingId(null);
        }
    };

    const deleteNote = async (noteId) => {
        if (!includeNotes) return;
        setNotePendingId(noteId);
        setNoteError("");
        try {
            await axios.delete(`${API_BASE_URL}/api/notes/${noteId}`, { withCredentials: true });
            setNotes((items) => items.filter((note) => note.id !== noteId));
            if (editingNoteId === noteId) {
                setEditingNoteId(null);
                setEditingNoteText("");
            }
        } catch (e) {
            setNoteError(e?.response?.data?.error || "Не удалось удалить заметку.");
        } finally {
            setNotePendingId(null);
        }
    };

    const addVideo = async () => {
        if (!includeVideos || !videoUrl.trim()) return;
        await axios.post(`${API_BASE_URL}/api/spots/${spotId}/videos`, { url: videoUrl, title: videoTitle }, { withCredentials: true });
        const videosRes = await axios.get(`${API_BASE_URL}/api/spots/${spotId}/videos`);
        setVideos(videosRes.data || []);
        setVideoTitle("");
        setVideoUrl("");
    };

    return {
        spotId,
        spot,
        setSpot,
        loading,
        message,
        isAuthenticated,
        reload: load,

        comments,
        setComments,
        visibleComments,

        selectedFish,
        setSelectedFish,
        forecast,
        setForecast,
        forecastLoading,
        forecastError,
        selectedForecastSlot,
        setSelectedForecastSlot,
        forecastTable,
        loadForecast,

        nearestWeather,
        nearestWeatherLoading,
        nearestWeatherError,
        weatherTimeline,
        weatherTimelineLoading,
        weatherTimelineError,
        weatherTimelineFetchedAt,
        weatherHourColumns,
        weatherForecastTable,
        activeWeather,
        weatherSource,
        weatherIconKey,
        precipitationValue,
        cloudinessValue,

        commentText,
        setCommentText,
        commentForm,
        setCommentForm,
        commentFiles,
        setCommentFiles,
        commentSubmitting,
        commentError,
        commentFieldErrors,
        commentFileErrors,
        commentFormOpen,
        setCommentFormOpen,
        hasCommentFileErrors,
        maxFishingDateTime,
        setCommentFormField,
        commentInputClass,
        validateCommentForm,
        handleCommentPhotoChange,
        submitComment,

        actionMessage,
        liked,
        favorited,
        likePending,
        favoritePending,
        toggleLike,
        toggleFavorite,

        reportModalOpen,
        setReportModalOpen,
        openReportModal,

        routes,
        selectedRouteIndex,
        setSelectedRouteIndex,
        selectedRoute,
        mapRoutes,

        videos,
        setVideos,
        videoUrl,
        setVideoUrl,
        videoTitle,
        setVideoTitle,
        addVideo,

        notes,
        setNotes,
        noteFormOpen,
        setNoteFormOpen,
        noteText,
        setNoteText,
        editingNoteId,
        setEditingNoteId,
        editingNoteText,
        setEditingNoteText,
        notePendingId,
        noteError,
        createNote,
        startEditNote,
        updateNote,
        deleteNote,
        reloadNotes,

        activeTab,
        setActiveTab,
        photoType,
        setPhotoType,
        weatherType,
        setWeatherType,
        showAllFish,
        setShowAllFish,

        mapTitle,
        editSuggestionPath,
        openLoginModalForComments,
        requireAuthForEditSuggestion,
        placeLabel,
        fish,
        placePhotos,
        catchPhotos,
        deepPhotos,
        photoSource,
        hasPhotos,
        heroImage,
        depthLabel,
        today,
        activeBans,
    };
}
